import React, { useEffect, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { opportunitiesService } from "../opportunities/opportunitiesService";
// TODO(api): Replace direct agentService import with API call
// import { agentService } from '../services/agentService';
import { notificationsService } from "../notifications/notificationsService";
// import { auditService } from "../services/auditService";
import { useAuth } from "../context/AuthContext";

const OpportunityApprovals = ({ setError, setSuccess }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const [opps, setOpps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOpp, setSelectedOpp] = useState(null);
    const [agent, setAgent] = useState(null);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const fetchOpps = useCallback(async () => {
        setLoading(true);
        try {
            const pending = await opportunitiesService.getPendingApprovalOpportunities();
            setOpps(pending);
        } catch (error) {
            // console.error("Error fetching pending opportunities:", error);
            setError && setError(`Failed to fetch opportunities: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        fetchOpps();
    }, [fetchOpps]);

    const handleView = async (op) => {
        setSelectedOpp(op);
        if (op.agentId) {
            // TODO(api): Replace direct agentService import with API call
            // const ag = await // TODO(api): Replace agentService usage with API call
            // agentService.getAgentById(op.agentId);
            setAgent(null);
        } else {
            setAgent(null);
        }
    };

    const handleApprove = async (op) => {
        try {
            await opportunitiesService.updateOpportunity(op.id, { 
                status: "approved",
                approvedBy: currentUser?.uid,
                approvedAt: new Date().toISOString()
            });
      
            setSuccess && setSuccess(t("admin.opportunityApproved", "Opportunity approved successfully."));
      
            // Send notification to agent
            if (op.agentId) {
                await notificationsService.sendOpportunityNotification(
                    op.agentId,
                    "approved",
                    op.id,
                    { opportunityTitle: op.title || op.titulo }
                );
            }
      
            // Log audit action
            // await auditService.logOpportunityApproved(
            //     currentUser?.uid, 
            //     op.id, 
            //     op.title || op.titulo
            // );
      
            fetchOpps();
            setSelectedOpp(null);
        } catch (err) {
            // console.error("Error approving opportunity:", err);
            setError && setError(`Error approving opportunity: ${err.message}`);
        }
    };

    const handleReject = async (op) => {
        setRejectDialog(true);
        setSelectedOpp(op);
    };

    const confirmReject = async () => {
        try {
            if (!rejectReason.trim()) {
                setError && setError("Rejection reason is required");
                return;
            }

            await opportunitiesService.updateOpportunity(selectedOpp.id, { 
                status: "rejected", 
                rejectReason: rejectReason.trim(),
                rejectedBy: currentUser?.uid,
                rejectedAt: new Date().toISOString()
            });
      
            setSuccess && setSuccess(t("admin.opportunityRejected", "Opportunity rejected successfully."));
      
            // Send notification to agent
            if (selectedOpp.agentId) {
                await notificationsService.sendOpportunityNotification(
                    selectedOpp.agentId,
                    "rejected",
                    selectedOpp.id,
                    { 
                        opportunityTitle: selectedOpp.title || selectedOpp.titulo,
                        reason: rejectReason 
                    }
                );
            }
      
            // Log audit action
            // await auditService.logOpportunityRejected(
            //     currentUser?.uid, 
            //     selectedOpp.id, 
            //     selectedOpp.title || selectedOpp.titulo,
            //     rejectReason
            // );
      
            fetchOpps();
            setSelectedOpp(null);
            setRejectDialog(false);
            setRejectReason("");
        } catch (err) {
            // console.error("Error rejecting opportunity:", err);
            setError && setError(`Error rejecting opportunity: ${err.message}`);
        }
    };

    const debugAllOpportunities = async () => {
        try {
            const all = await opportunitiesService.getAllOpportunities();
      
            // Group by status
            const byStatus = all.reduce((acc, opp) => {
                const status = opp.status || "no-status";
                if (!acc[status]) acc[status] = [];
                acc[status].push(opp);
                return acc;
            }, {});
      
            let message = `Found ${all.length} total opportunities:\n`;
            Object.entries(byStatus).forEach(([status, opps]) => {
                message += `\n${status}: ${opps.length} opportunities`;
                opps.forEach(opp => {
                    message += `\n  - ${opp.title || opp.titulo || "No title"} (ID: ${opp.id})`;
                });
            });
      
            alert(message);
        } catch (error) {
            // console.error("Debug error:", error);
            alert("Error in debug: " + error.message);
        }
    };

    const showAllOpportunities = async () => {
        try {
            const all = await opportunitiesService.getAllOpportunities();
            setOpps(all); // Temporarily show all opportunities
        } catch (error) {
            // console.error("Error showing all opportunities:", error);
            setError && setError(`Error showing all opportunities: ${error.message}`);
        }
    };

    if (loading) return <CircularProgress />;
  
    if (opps.length === 0) {
        return (
            <Box>
                <Typography>{t("admin.noPendingOpportunities", "No pending opportunities.")}</Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Button variant="outlined" onClick={debugAllOpportunities}>Debug: Check All Opportunities</Button>
                    <Button variant="outlined" onClick={showAllOpportunities}>Show All Opportunities (Debug)</Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button variant="outlined" onClick={debugAllOpportunities}>Debug: Check All Opportunities</Button>
                <Button variant="outlined" onClick={showAllOpportunities}>Show All Opportunities (Debug)</Button>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("admin.title", "Title")}</TableCell>
                            <TableCell>{t("admin.agent", "Agent")}</TableCell>
                            <TableCell>{t("admin.amount", "Amount")}</TableCell>
                            <TableCell>{t("admin.date", "Date")}</TableCell>
                            <TableCell>{t("admin.actions", "Actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {opps.map(op => (
                            <TableRow key={op.id}>
                                <TableCell>{op.title || op.titulo || "No title"}</TableCell>
                                <TableCell>
                                    <Button onClick={() => handleView(op)}>{t("admin.viewAgent", "View Agent")}</Button>
                                </TableCell>
                                <TableCell>
                                    {op.amount || op.monto ? `€${(op.amount || op.monto).toLocaleString()}` : "-"}
                                </TableCell>
                                <TableCell>
                                    {op.createdAt ? new Date(op.createdAt).toLocaleString() : "-"}
                                </TableCell>
                                <TableCell>
                                    <Button 
                                        color="success" 
                                        variant="contained" 
                                        sx={{ mr: 1 }} 
                                        onClick={() => handleApprove(op)}
                                    >
                                        {t("admin.approve", "Approve")}
                                    </Button>
                                    <Button 
                                        color="error" 
                                        variant="outlined" 
                                        onClick={() => handleReject(op)}
                                    >
                                        {t("admin.reject", "Reject")}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
      
            {/* Agent Details Dialog */}
            <Dialog open={!!selectedOpp && !!agent} onClose={() => { setSelectedOpp(null); setAgent(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>{t("admin.agentDetails", "Agent Details")}</DialogTitle>
                <DialogContent>
                    <Typography><b>{t("agents.name", "Name")}:</b> {agent?.nombre}</Typography>
                    <Typography><b>{t("agents.email", "Email")}:</b> {agent?.email}</Typography>
                    <Typography><b>{t("agents.phone", "Phone")}:</b> {agent?.telefono}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setSelectedOpp(null); setAgent(null); }}>{t("common.close", "Close")}</Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("admin.rejectOpportunity", "Reject Opportunity")}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {t("admin.rejectReasonRequired", "Please provide a reason for rejecting this opportunity:")}
                    </Typography>
                    <TextField
                        label={t("admin.rejectionReason", "Rejection Reason")}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog(false)}>{t("common.cancel", "Cancel")}</Button>
                    <Button onClick={confirmReject} color="error" variant="contained">
                        {t("admin.confirmReject", "Confirm Rejection")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OpportunityApprovals; 